import { action, observable } from 'mobx';
import stores from '../../../models/Stores';
import {
  Geometry,
  Graphic,
  GraphicsLayer,
  MapView,
  Polygon,
  Point,
  Sketch,
  SpatialReference,
  esriProjection,
  SimpleMarkerSymbol,
} from 'cd-eft/utils/esriTypes';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import { IpostGetElevationData } from 'cd-eft/models/SurveyEngineering/getElevation';
import { IgenerateLandLevelingPostData } from 'cd-eft/models/SurveyEngineering/generateLandLeveling';
import { IpostGetCentroidData } from 'cd-eft/models/SurveyEngineering/getCentroid';
import { IpostGetBestFitData } from 'cd-eft/models/SurveyEngineering/getBestFit';
import { IupdateDesignElementPostData } from 'cd-eft/models/SurveyEngineering/updateDesignElement';
import { IaddDesignElementPostData } from 'cd-eft/models/SurveyEngineering/addDesignElement';
import { removeLayers } from 'cd-eft/utils/gisUtils';
import * as SETAPI from 'cd-eft/services/eftSetApi';
import { toDecimalPrecision } from 'cd-eft/utils/mathUtilities';
import { DesignContainerVM } from 'cd-eft/components/Design/DesignContainer/DesignContainerVM';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { appConfig } from 'cd-eft/config/AppConfig';
import { AppConstants } from 'cd-eft/appConstants';
import FeatureSnappingLayerSource from '@arcgis/core/views/interactive/snapping/FeatureSnappingLayerSource';
import { SnapTooltipProvider } from '../../../framework/SnapTooltipProvider/SnapTooltipProvider'
import { createSaveThumbnail } from 'cd-eft/utils/structureLayerUtils';


const designController = SETAPI.DesignElementController;
const structureController = SETAPI.StructureController;

const libraryStore = stores.AppStore.projectsLibraryStore;
const { landLevelingDesignStore, designStore } = stores.AppStore;

const polygonSymbol = new SimpleFillSymbol({
  // type: 'simple-fill', // autocasts as new SimpleFillSymbol()
  color: [255, 255, 255, 0],
  outline: {
    // autocasts as new SimpleLineSymbol()
    color: 'red',
    width: 4,
  },
});

export class NewLandLevelingVM {
  private mapView?: MapView;
  public sketchWidget?: Sketch;
  public bpSketchWidget?: Sketch;
  private surfaceModified: boolean = false;
  private _currentWkid = appConfig.tempHardCodes.wkid;
  public _snapTooltipProvider: SnapTooltipProvider | undefined = void 0;
  private _isUpdating: boolean = false;
  private _sketchHandle;
  private _bpSketchHandle;

  private readonly llGraphics = new GraphicsLayer({ id: 'llGraphics', title: 'llGraphics' });
  private readonly bpGraphics = new GraphicsLayer({ id: 'bpGraphics', title: 'Base Point' });
  private readonly tempGraphics = new GraphicsLayer({ id: 'tempGraphics', title: 'Base Point' });
  private mounted: boolean = false;
  props;

  constructor(props: any) {
    this.props = props;
    if (props.mapView) {
      this.mapView = props.mapView;
      this.sketchWidget = props.sketchWidget;
      const surveyPointsLayer = this.mapView?.map.findLayerById(AppConstants.SurveyPointsLayerId) as GraphicsLayer;
      const centerPointGraphicLayer = this.mapView?.map.findLayerById("centerPointGraphic");
      const fsls = new FeatureSnappingLayerSource({
        enabled: true,
        // Use surveyPointsLayer if defined, else use centerPointGraphicLayer
        layer: surveyPointsLayer ?? centerPointGraphicLayer,
      } as any);
      this.onMouseEnter = this.onMouseEnter.bind(this);

      const sketchWidget = new Sketch({
        view: this.mapView,
        layer: this.llGraphics,
        creationMode: 'single',
        visibleElements: {
          createTools: {
            point: false,
            polyline: false,
            polygon: true,
            rectangle: false,
            circle: false,
          },
          selectionTools: {
            'rectangle-selection': false,
            'lasso-selection': false,
          },
          settingsMenu: false,
          undoRedoMenu: false,
        } as any,
        defaultUpdateOptions: {
          enableScaling: true,
          enableRotation: true,
          toggleToolOnClick: true,
          preserveAspectRatio: true,
        },
        snappingOptions: {
          enabled: false,
          featureSources: [fsls],
          distance: 21,
          selfEnabled: false
        }
      });
      sketchWidget.viewModel.polygonSymbol = polygonSymbol;
      sketchWidget.on('create', async (event) => {
        if (sketchWidget.state === 'active') {
          this.llGraphics.removeAll();
        }
        if (event.state === 'complete') {
          if (this._snapTooltipProvider) {
            this._snapTooltipProvider.stop();
            this._snapTooltipProvider = undefined;
          }
          this.polyToPoints(this.mapView, sketchWidget, this.llGraphics);
        }
        if (event.state === 'cancel') {
          if (this._snapTooltipProvider) {
            this._snapTooltipProvider.stop();
            this._snapTooltipProvider = undefined;
          }
        }
      });
      sketchWidget.on('update', async (event) => {
        if (event.state === "start") {
          this._isUpdating = true;
        }
        if (event.state === "complete") {
          this.polyToPoints(this.mapView, sketchWidget, this.llGraphics);
          this._isUpdating = false;
        }

      });
      sketchWidget.on('delete', async (event) => {
        landLevelingDesignStore.boundaryPoints = [];
        this.newLandLevelingForm.boundaryPoints = [];
      });

      this._sketchHandle = sketchWidget.watch('state', () => { this.activeSketchHandler(sketchWidget, surveyPointsLayer) });

      this.sketchWidget = sketchWidget;

      const bpSketchWidget = new Sketch({
        view: this.mapView,
        layer: this.tempGraphics,
        creationMode: 'single',
        visibleElements: {
          createTools: {
            point: true,
            polyline: false,
            polygon: false,
            rectangle: false,
            circle: false,
          },
          selectionTools: {
            'rectangle-selection': false,
            'lasso-selection': false,
          },
          settingsMenu: false,
          undoRedoMenu: false,
        } as any,
        defaultUpdateOptions: {
          enableScaling: false,
          enableRotation: false,
          toggleToolOnClick: false,
          preserveAspectRatio: false,
          reshapeOptions: {
            shapeOperation: 'none'
          }
        } as any,
        snappingOptions: {
          enabled: false,
          featureSources: [fsls],
          distance: 21,
          selfEnabled: false
        }
      });
      bpSketchWidget.on('create', async (event) => {
        if (event.state === 'complete') {
          if (this._snapTooltipProvider) {
            this._snapTooltipProvider.stop();
            this._snapTooltipProvider = undefined;
          }
          this.bpGraphics.removeAll();
          this.getBasePoint(bpSketchWidget, this.bpGraphics);
          // if (this.lockBasePointZ) {
          //   // this.toggleLockBasePointZ();
          // }
        }
        if (event.state === 'cancel') {
          if (this._snapTooltipProvider) {
            this._snapTooltipProvider.stop();
            this._snapTooltipProvider = undefined;
          }
        }
      });

      this._bpSketchHandle = bpSketchWidget.watch('state', () => { this.activeSketchHandler(bpSketchWidget, surveyPointsLayer) });

      this.bpSketchWidget = bpSketchWidget;
    }
  }

  @action.bound activeSketchHandler(widget, lyr) {
    if (widget.state === 'active') {
      if (!this._snapTooltipProvider) {
        this._snapTooltipProvider = new SnapTooltipProvider(this.mapView, lyr, libraryStore.projectSpatialReference!);
        // Ensure lyr is not undefined
        if (lyr) {
          this._snapTooltipProvider.getSurveyPoints(lyr);
        }
      }
      if (designStore.snappingEnabled) this._snapTooltipProvider?.start();
    }
  }

  @action.bound toggleLandLevelSnapping() {
    if (this.mounted) {
      if (!designStore.snappingEnabled) {
        this.bpSketchWidget!.snappingOptions.enabled = false;
        this.sketchWidget!.snappingOptions.enabled = false;
        this._snapTooltipProvider?.stop();
      } else {
        this.bpSketchWidget!.snappingOptions.enabled = true;
        this.sketchWidget!.snappingOptions.enabled = true;
        if (this.bpSketchWidget?.state === "active" && !this._isUpdating || this.sketchWidget?.state === "active" && !this._isUpdating) this._snapTooltipProvider?.start();
      }

    }
  }

  @action.bound onMount(history) {
    this.mounted = true;
    landLevelingDesignStore.showLoader = false;

    if (designStore.snappingEnabled) this.toggleLandLevelSnapping();

    this.props.setCallables({
      toggleSnapping: this.toggleLandLevelSnapping
    });

    if (!designStore.hasProject()) {
      //No project
      // landLevelingDesignStore.showNotification(designStore.noProjectMessage, 'Uh oh!', 'warn');
      MessageCenter.addWarning(designStore.noProjectMessage, [
        {
          callback: () => {
            history?.push('/projects/new-project');
            MessageCenter.clearMessages();
          },
          text: 'Go Create a Project',
        },
      ]);
    } else if (!designStore.hasSurfaceTest()) {
      //NO SURFACE
      // landLevelingDesignStore.showNotification(designStore.noSurfaceMessage, 'Uh oh!', 'warn');
      MessageCenter.addWarning(designStore.noSurfaceMessage, [
        {
          callback: () => {
            history?.push(`/design/${designStore.projectsStore.currentProject.projectUuid}/surface`);
            MessageCenter.clearMessages();
          },
          text: 'Go Create a Surface',
        },
      ]);
    } else {
      // landLevelingDesignStore.clearNotification();
      MessageCenter.clearMessages();
    }

    if (this.mapView) {
      this.mapView.map.add(this.llGraphics);
      this.mapView.map.add(this.bpGraphics);
    }
  }

  @action.bound onUnmount() {
    this.mounted = false;
    this.llGraphics.graphics.removeAll();
    this.bpGraphics.graphics.removeAll();
    this._snapTooltipProvider?.Dispose();
    this._bpSketchHandle?.remove();
    this._sketchHandle?.remove();
    if (this.mapView && this.mapView.map) {
      this.mapView.map.remove(this.llGraphics);
      this.mapView.map.remove(this.bpGraphics);
    }
  }

  @observable error: boolean = false;
  @observable errorMsg: string = '';
  @observable isLoading: boolean = false;
  @observable excluded = {
    centroid: false,
    bestFit: false,
  };
  @observable overrideReturnedValue: boolean = false;
  @observable overrideUserValue: boolean = false;
  @observable isHovered = false;
  @observable errorMessage: string = '';

  @observable showOverrideButtons: boolean = false;
  @observable sketchWidgetVisible: boolean = true;
  @observable newLandLevelingForm = landLevelingDesignStore.newDesignForm;
  // @observable lockBasePointZ: boolean = false;

  // Form Validation model
  @observable formValidation = {
    formErrors: {
      name: '',
      basePointx: '',
      basePointy: '',
      basePointz: '',
      yslope: '',
      xslope: '',
      borrow: '',
      waste: '',
      cutFillRatio: '',
      boundaryPoints: '',
      useWholeField: '',
      llStakeoutGrid: '',
      bufferSize: '',
    },
    nameValid: false,
    basePointxValid: false,
    basePointyValid: false,
    basePointzValid: false,
    //the fields below are optional. Default value is ok.
    yslopeValid: true,
    xslopeValid: true,
    borrowValid: true,
    wasteValid: true,
    cutFillRatioValid: true,
    boundaryPointsValid: true,
    useWholeFieldValid: true,
    formValid: false,
    llStakeoutGrid: true,
    bufferSize: true,
  };

  @action async onMouseEnter() {
    this.isHovered = true;
    this.errorMessage = await MessageCenter.addErrorLabelFromCode('LandLevel_10');
  }
  onMouseLeave = action(() => {
    this.isHovered = false;
  });
  @action.bound async checkStructurePendingEdits(inputId) {
    landLevelingDesignStore.pendingEdits.unsavedEdits = true;
    if (landLevelingDesignStore.newDesign || landLevelingDesignStore.editDesign) {
      if (inputId === 'name') {
        landLevelingDesignStore.pendingEdits.name = true;
      } else {
        landLevelingDesignStore.pendingEdits.design = true;
      }
    }
  }

  @action.bound async getBasePoint(bpSketchWidget, bpGraphics) {
    const spatialReference = libraryStore.projectSpatialReference!;
    const lyr: GraphicsLayer = bpSketchWidget.layer;
    const rawGeom = lyr.graphics.getItemAt(lyr.graphics.length - 1).geometry;

    await esriProjection.load();
    const geom = esriProjection.project(rawGeom, spatialReference) as Geometry;
    const graphic = new Graphic({ geometry: geom });

    const projectedGeom = graphic.geometry;
    const polyPoints = projectedGeom.toJSON();

    lyr.graphics.removeAll();
    landLevelingDesignStore.newDesignForm.basePoint.x = toDecimalPrecision(parseFloat(polyPoints.x), 3);
    landLevelingDesignStore.newDesignForm.basePoint.y = toDecimalPrecision(parseFloat(polyPoints.y), 3);

    if (designStore.hasProject() && designStore.hasSurfaceTest()) {
      const postJSON: IpostGetElevationData = {
        basePoint: {
          x: landLevelingDesignStore.newDesignForm.basePoint.x,
          y: landLevelingDesignStore.newDesignForm.basePoint.y,
        },
        designElementId: designStore.currentSurface.designElementId,
      };
      try {
        const response = (await structureController.getElevationUsingPOST(postJSON)).data;
        if (response.basePoint) {
          landLevelingDesignStore.newDesignForm.basePoint.z = +response.basePoint.z!.toFixed(3);
        }
        landLevelingDesignStore.chooseCenter = false;

        const markerSymbol = new SimpleMarkerSymbol({
          color: [255, 0, 0],
          size: 10,
          outline: {
            color: [0, 0, 0],
            width: 0.5,
          },
        });
        const basePoint: any = new Point();
        basePoint.spatialReference = spatialReference;
        basePoint.hasZ = false;
        basePoint.x = polyPoints.x;
        basePoint.y = polyPoints.y;

        this.checkStructurePendingEdits("basePoint");
        const basePointGraphic = new Graphic({
          geometry: basePoint,
          symbol: markerSymbol,
          attributes: {
            pointName: 'center-marker',
            y: landLevelingDesignStore.newDesignForm.basePoint.y,
            x: landLevelingDesignStore.newDesignForm.basePoint.x,
            description: 'Marker for base point tool.',
          },
        });

        this.tempGraphics.graphics.removeAll();
        this.bpGraphics.graphics.add(basePointGraphic);
        landLevelingDesignStore.chooseCenter = true;
        landLevelingDesignStore.clearNotification();
      } catch (error) {
        this.showOverrideButtons = false;
        if ((error as any).response && (error as any).response.data && (error as any).response.data.errorMessage) {
          MessageCenter.addError((error as any).response.data.errorMessage, [], (error as any).response.data.originalErrorCode);
        } else {
          MessageCenter.addError((error as any).message);
        }
      }
    }
  }

  @action.bound handleOnBlurRounding(event) {
    const { target } = event;
    let { value } = target;
    const { id, name } = target;
    // if value is number keep it a number
    //If user input is null, reset grid value and cut/fill to be default.
    if (id.includes('Grid') && isNaN(parseFloat(value))) {
      value = 100;
    }
    if (id.includes('cut') && isNaN(parseFloat(value))) {
      value = 1;
    }
    if (!isNaN(parseFloat(value)) && isFinite(value) && !target.id.includes('name')) {
      value = +value;
    }
    if (id.includes('basePoint')) {
      const newName = id.replace('basePoint', '');
      this.newLandLevelingForm.basePoint[newName] = toDecimalPrecision(value, 3);
    } else if (id.includes('slope')) {
      this.newLandLevelingForm[id] = toDecimalPrecision(value, 6);
    } else {

      this.newLandLevelingForm[target.name] = toDecimalPrecision(value, 3);
    }
    this.validateField(name, value);
  }

  // @action.bound toggleLockBasePointZ() {
  //   this.lockBasePointZ = !this.lockBasePointZ;
  // }

  @action.bound async handleInputChange(event) {
    const { target } = event;
    let { value } = target;
    const { name, id } = target;
    // if value is number keep it a number
    if (!isNaN(parseFloat(value)) && isFinite(value) && !target.id.includes('name')) {
      value = +value;
    }

    this.checkStructurePendingEdits(id);

    if (
      id.includes('basePointz') &&
      !this.overrideReturnedValue &&
      (landLevelingDesignStore.newDesignForm.bestFit || landLevelingDesignStore.editDesign)
    ) {
      this.showOverrideButtons = true;
      // landLevelingDesignStore.showNotification('Best Cut/Fill ratio is not reached. Override?', 'Warning', 'warn');
      await MessageCenter.addWarningFromCode('LandLevel_05', [
        { callback: () => this.overrideZ(true), text: 'Yes' },
        { callback: () => this.overrideZ(false), text: 'No' },
      ]);
      if (!this.overrideReturnedValue) {
        return;
      }
    }

    if (id.includes('basePoint')) {
      const newName = id.replace('basePoint', '');
      this.newLandLevelingForm.basePoint[newName] = value;
    } else {
      this.newLandLevelingForm[name] = value;
    }
    if (id === 'llStakeoutGrid') {
      this.newLandLevelingForm.llStakeoutGrid = value;

    }
    if (id === 'bufferSize') {
      this.newLandLevelingForm.bufferSize = value;
    }
    this.validateField(name, value);
  }

  @action.bound overrideZ(isOverrode: boolean) {
    if (isOverrode && !this.overrideReturnedValue) {
      this.overrideReturnedValue = true;
      this.overrideUserValue = false;
      // landLevelingDesignStore.clearNotification();
      MessageCenter.clearMessages();
    } else if (isOverrode && this.overrideReturnedValue) {
      this.overrideReturnedValue = false;
      this.overrideUserValue = true;
      // landLevelingDesignStore.clearNotification();
      MessageCenter.clearMessages();
    } else if (!isOverrode) {
      // landLevelingDesignStore.clearNotification();
      MessageCenter.clearMessages();
    }
    this.showOverrideButtons = false;
  }

  @action.bound toggleWholeField(mapView) {
    this.newLandLevelingForm.useWholeField = !this.newLandLevelingForm.useWholeField;
    // When Whole Field is selected, set buffer to 0
    if (this.newLandLevelingForm.useWholeField) {
      this.newLandLevelingForm.bufferSize = 0;
    }
    const layer = mapView.map.findLayerById('llGraphics');
    if (layer) {
      layer.graphics.removeAll();
      const bPts: IpointData[] = [];
      for (let itm = 0; itm < designStore.currentSurface.borderVector.length; itm++) {
        const bp: IpointData = {
          pointName: itm.toString(),
          x: designStore.currentSurface.borderVector[itm].x,
          y: designStore.currentSurface.borderVector[itm].y,
          z: 0,
        };
        bPts.push(bp);
      }
      landLevelingDesignStore.boundaryPoints = bPts;
      this.newLandLevelingForm.boundaryPoints = bPts;

      //draw boundary

      const spatialReference = libraryStore.projectSpatialReference!;
      const wholeFieldBoundaryPoints = [] as any;
      for (let pt = 0; pt < landLevelingDesignStore.boundaryPoints.length; pt++) {
        const point = new Point({
          spatialReference: spatialReference,
          hasZ: true,
          x: landLevelingDesignStore.boundaryPoints[pt].x,
          y: landLevelingDesignStore.boundaryPoints[pt].y,
          z: landLevelingDesignStore.boundaryPoints[pt].z,
        });
        wholeFieldBoundaryPoints!.push(point);
      }
      const wholeFieldBoundaryPolygon = new Polygon();
      wholeFieldBoundaryPolygon.spatialReference = spatialReference;
      wholeFieldBoundaryPolygon.addRing(wholeFieldBoundaryPoints);

      const polygonGraphic = new Graphic({
        geometry: wholeFieldBoundaryPolygon,
        symbol: polygonSymbol,
      });

      layer.graphics.add(polygonGraphic);
      mapView.map.add(layer, 3);
    }
    this.validateField('useWholeField', this.newLandLevelingForm.useWholeField);
    this.validateField('bufferSize', this.newLandLevelingForm.bufferSize);
  }

  @action.bound async polyToPoints(mapView, sketchWidget, gLyr) {
    if (sketchWidget.layer.graphics.length > 0) {
      landLevelingDesignStore.boundaryPoints = [];
      const bPts: IpointData[] = [];
      this.newLandLevelingForm.useWholeField = false;
      const spatialReference = libraryStore.projectSpatialReference!;
      const lyr: GraphicsLayer = sketchWidget.layer;
      const rawGeom = lyr.graphics.getItemAt(0).geometry;
      await esriProjection.load();
      const geom = esriProjection.project(rawGeom, spatialReference) as Geometry;
      const graphic = new Graphic({ geometry: geom });
      const projectedGeom = graphic.geometry;
      const polyPoints = projectedGeom.toJSON().rings[0];
      for (let itm = 0; itm < polyPoints.length; itm++) {
        const bp: IpointData = {
          pointName: itm.toString(),
          x: polyPoints[itm][0],
          y: polyPoints[itm][1],
          z: 0,
        };
        bPts.push(bp);
      }
      landLevelingDesignStore.boundaryPoints = bPts;
      landLevelingDesignStore.landLevelingBoundary = lyr;
      this.newLandLevelingForm.boundaryPoints = bPts;
      this.validateField('boundaryPoints', this.newLandLevelingForm.boundaryPoints);
      landLevelingDesignStore.boundaryLayer = gLyr;
    }
  }

  @action.bound toggleCentroid(value: boolean) {
    this.newLandLevelingForm.centroid = value;
  }

  @action.bound async getErrorMessage(code: string, container, fieldName: string) {
    if (code === '') {
      container[fieldName] = '';
    } else {
      let message = await MessageCenter.addErrorLabelFromCode(code);
      container[fieldName] = message;

      if (fieldName.includes('basePointx') && message != '') {
        message = "Base Point X" + message;
        container[fieldName] = message;
      }
      if (fieldName.includes('basePointy') && message != '') {
        message = "Base Point Y" + message;
        container[fieldName] = message;
      }
      if (fieldName.includes('basePointz') && message != '') {
        message = "Base Point Z" + message;
        container[fieldName] = message;
      }
      if (fieldName.includes('boundaryPoints') && message != '') {
        message = "Boundary" + message;
        container[fieldName] = message;
      }
      if (fieldName.includes('useWholeField') && message != '') {
        message = "Boundary" + message;
        container[fieldName] = message;
      }
      if (fieldName.includes('boundaryPoints') && container['useWholeField'] != '') {
        container['useWholeField'] = '';
      }
      if (fieldName.includes('useWholeField') && container['boundaryPoints'] != '') {
        container['useWholeField'] = '';
      }
      if (fieldName.includes('borrow') && message != '') {
        message = "Borrow" + message;
        container[fieldName] = message;
      }
      if (fieldName.includes('waste') && message != '') {
        message = "Waste" + message;
        container[fieldName] = message;
      }
      if (fieldName.includes('cutFillRatio') && message != '') {
        message = "Cut/Fill Ratio " + message;
        container[fieldName] = message;
      }
      if (fieldName.includes('llStakeoutGrid') && message != '') {
        message = "StakeoutGrid" + message;
        container[fieldName] = message;
      }
      if (fieldName.includes('bufferSize') && message != '') {
        message = "Buffer distance" + message;
        container[fieldName] = message;
      }
    }
    return;
  }

  @action.bound async checkNameDuplicate() {
    const nameDuplicate = landLevelingDesignStore.landLevelingSummaryTable.some((summary) => {
      return summary.designElementName.toLowerCase() === landLevelingDesignStore.newDesignForm.name.toLowerCase() &&
        landLevelingDesignStore.newDesignForm.designElementId !== summary.designElementId;
    });
    if (nameDuplicate) {
      /*       const errorCode = 'Common_07';
            const fieldName = 'name'
            await this.getErrorMessage(errorCode, this.formValidation.formErrors, fieldName); */
      await MessageCenter.addErrorFromCode('Common_07');
      return true;
    }
    return false;
  }

  @action.bound async generateStructure() {
    this.exclude('none');
    if (!this.formValidation.formValid) {
      // Loop validates field if good in place of user interaction\
      for (const field in this.formValidation.formErrors) {
        if (field.includes('basePoint')) {
          const newName = field.replace('basePoint', '');
          const value = this.newLandLevelingForm.basePoint[newName];
          this.validateField(field, value);
        } else {
          const value = this.newLandLevelingForm[field];
          this.validateField(field, value);
        }
      }
      const checkNameDuplicate = await this.checkNameDuplicate();

      // If invalid - return and halt execution
      if (!this.formValidation.formValid || checkNameDuplicate) {
        return;
      }
    }
    const postLandLevelingJSON: IgenerateLandLevelingPostData = {
      name: this.newLandLevelingForm.name,
      type: this.newLandLevelingForm.type,
      basePoint: this.newLandLevelingForm.basePoint,
      xslope: this.newLandLevelingForm.xslope,
      yslope: this.newLandLevelingForm.yslope,
      borrow: this.newLandLevelingForm.borrow,
      waste: this.newLandLevelingForm.waste,
      cutFillRatio: this.newLandLevelingForm.cutFillRatio,
      designElementId: designStore.currentSurface.designElementId,
      useWholeField: this.newLandLevelingForm.useWholeField,
      boundaryPoints: this.newLandLevelingForm.boundaryPoints,
      llStakeoutGrid: this.newLandLevelingForm.llStakeoutGrid,
      bufferSize: this.newLandLevelingForm.bufferSize

    };
    landLevelingDesignStore.showLoader = true;
    this.isLoading = true;
    this.error = false;
    try {
      const response = (await structureController.designLandLevelingUsingPOST(postLandLevelingJSON)).data;
      if (response.designElementId && this.mapView) {
        landLevelingDesignStore.newLandLevelingResults = response as any;
        landLevelingDesignStore.newDesign = true;

        designStore.currentDesignElementJson = response as any;
        designStore.currentDesignElementJson.designedContours = null;
        designStore.currentDesignElementJson.designedTinTriangles = null;

        if (landLevelingDesignStore.newLandLevelingResults) {
          // if (this.lockBasePointZ) {
          //   landLevelingDesignStore.newLandLevelingResults.basePoint.z = this.newLandLevelingForm.basePoint.z;
          // } else {
          landLevelingDesignStore.newLandLevelingResults.basePoint.z = toDecimalPrecision(
            landLevelingDesignStore.newLandLevelingResults.basePoint.z,
            3
          );
          this.newLandLevelingForm.basePoint.z = landLevelingDesignStore.newLandLevelingResults.basePoint.z;
          // }
        }
        if (landLevelingDesignStore.currentDesignLayer !== undefined) {
          this.mapView.map.remove(landLevelingDesignStore.currentDesignLayer);
        }
        const surfaceModified = await DesignContainerVM.loadSurfaceFromData(
          response.designedTinTriangles!,
          undefined,
          response.designedContours
        );
        this.surfaceModified = this.surfaceModified || surfaceModified;
        landLevelingDesignStore.displayLandLevelingGraphic(this.mapView, response, true, libraryStore.projectSpatialReference!);
      }
      // landLevelingDesignStore.clearNotification();
      MessageCenter.clearMessages();
      landLevelingDesignStore.showLoader = false;
      this.isLoading = false;
      this.error = false;
      designStore.structureLayerExists = true;
    } catch (error) {
      this.showOverrideButtons = false;
      if ((error as any).response && (error as any).response.data && (error as any).response.data.errorMessage) {
        MessageCenter.addError((error as any).response.data.errorMessage, [], (error as any).response.data.originalErrorCode);
      } else {
        MessageCenter.addError((error as any).message);
      }
      landLevelingDesignStore.showLoader = false;
      this.error = true;
      this.isLoading = false;
    }
    if (!this.error) {
      //pending edits have been designed, reset flags
      landLevelingDesignStore.pendingEdits.name = false;
      landLevelingDesignStore.pendingEdits.design = false;
      landLevelingDesignStore.pendingEdits.unsavedEdits = true;
    }
  }

  @action.bound resetOriginalLandLevelingDesignLayer() {
    if (
      landLevelingDesignStore.originalDesignLayer &&
      landLevelingDesignStore.editDesign &&
      landLevelingDesignStore.currentDesignLayer !== landLevelingDesignStore.originalDesignLayer
    ) {
      const index = landLevelingDesignStore.landLevelingLayers.findIndex(
        (layer) => layer.id === landLevelingDesignStore.currentDesignLayer.id
      );
      // pitDesignStore.pitLayers[index] = pitDesignStore.originalDesignLayer;
      landLevelingDesignStore.landLevelingLayers.splice(index, 1);
      removeLayers([landLevelingDesignStore.currentDesignLayer], this.mapView!);
      // pitDesignStore.currentDesignLayer = pitDesignStore.originalDesignLayer;
      landLevelingDesignStore.newDesignForm = landLevelingDesignStore.originalLandLevelingResults;
      landLevelingDesignStore.displayLandLevelingGraphic(
        this.mapView!,
        landLevelingDesignStore.newDesignForm,
        true,
        libraryStore.projectSpatialReference!,
        landLevelingDesignStore.originalIdString
      );
    }
  }

  @action.bound async cancelAddDesignElement(mapView, resetMostRecent: boolean = false) {
    designStore.onNewStructureTab = false;
    designStore.hideCrossSectionToolbar = true;
    designStore.hideSingleCrossSection = true;
    if (resetMostRecent) {
      this.resetOriginalLandLevelingDesignLayer();
    }
    const summaryEntry = landLevelingDesignStore.landLevelingSummaryTable.find(
      (entry) => entry.designElementId.toString() === landLevelingDesignStore.currentDesignLayer.id
    );
    if (this.mapView && (!summaryEntry || !summaryEntry.checked)) {
      removeLayers([landLevelingDesignStore.boundaryLayer], this.mapView);
      removeLayers([landLevelingDesignStore.currentDesignLayer], this.mapView);
    }
    if (this.mapView && !designStore.hideSingleCrossSection) {
      await designStore.validateSingleCrossSection(this.mapView);
    }
    if (resetMostRecent && this.surfaceModified) {
      this.surfaceModified = !(await DesignContainerVM.displayOriginalSurface());
    }
    designStore.currentDesignElementJson = null;
    landLevelingDesignStore.cancelDesignElement();
  }

  @action.bound async postGetCentroid() {
    this.exclude('centroid');

    // If BasePoint Z has changed by user, toggle notification
    // Changed condition to align with handleInputChange z condition
    if (
      !this.overrideReturnedValue &&
      (landLevelingDesignStore.newDesignForm.bestFit || landLevelingDesignStore.editDesign)
    ) {
      this.showOverrideButtons = true;
      // landLevelingDesignStore.showNotification('Best Cut/Fill ratio is not reached. Override?', 'Warning', 'warn');
      await MessageCenter.addWarningFromCode('LandLevel_05', [
        { callback: () => this.overrideZ(true), text: 'Yes' },
        { callback: () => this.overrideZ(false), text: 'No' },
      ]);
      if (!this.overrideReturnedValue) {
        return;
      }
    }

    // if (this.lockBasePointZ) {
    //   this.toggleLockBasePointZ();
    // }

    const postGetCentroidJSON: IpostGetCentroidData = {
      centroid: true,
      designElementId: designStore.currentSurface.designElementId,
      useWholeField: this.newLandLevelingForm.useWholeField,
      boundaryPoints: this.newLandLevelingForm.boundaryPoints,
    };
    landLevelingDesignStore.showLoader = true;
    this.isLoading = true;
    this.error = false;
    this.errorMsg = '';
    try {
      const response = (await structureController.getLandLevelingCentroidUsingPOST(postGetCentroidJSON)).data;

      if (response.basePoint) {
        this.newLandLevelingForm.basePoint.x = toDecimalPrecision(response.basePoint.x!, 3);
        this.newLandLevelingForm.basePoint.y = toDecimalPrecision(response.basePoint.y!, 3);
        this.newLandLevelingForm.basePoint.z = toDecimalPrecision(response.basePoint.z!, 3);
        this.newLandLevelingForm.centroid = response.centroid;
        this.toggleCentroid(true);
      }
      // want to clear and redraw basspoint graphic
      const markerSymbol = new SimpleMarkerSymbol({
        color: [255, 0, 0],
        size: 10,
        outline: {
          color: [0, 0, 0],
          width: 0.5,
        },
      });
      const basePoint: any = new Point();
      basePoint.spatialReference = libraryStore.projectSpatialReference;
      basePoint.hasZ = false;
      basePoint.x = response.basePoint!.x!;
      basePoint.y = response.basePoint!.y!;

      const basePointGraphic = new Graphic({
        geometry: basePoint,
        symbol: markerSymbol,
        attributes: {
          pointName: 'center-marker',
          y: landLevelingDesignStore.newDesignForm.basePoint.y,
          x: landLevelingDesignStore.newDesignForm.basePoint.x,
          description: 'Marker for base point tool.',
        },
      });

      this.bpGraphics.graphics.removeAll();
      this.bpGraphics.graphics.add(basePointGraphic);

      landLevelingDesignStore.showLoader = false;
      this.isLoading = false;
      this.error = false;
      this.errorMsg = '';
      if (landLevelingDesignStore.editDesign || landLevelingDesignStore.newDesign) {
        this.checkStructurePendingEdits('basePoint');
      }
    } catch (error) {
      this.showOverrideButtons = false;
      if ((error as any).response && (error as any).response.data && (error as any).response.data.errorMessage) {
        MessageCenter.addError((error as any).response.data.errorMessage, [], (error as any).response.data.originalErrorCode);
      } else {
        MessageCenter.addError((error as any).message);
      }
      landLevelingDesignStore.showLoader = false;
      this.toggleCentroid(false);
      this.error = true;
      this.isLoading = false;
    }
    //verify the form is valid after base points are posted.
    if (!this.formValidation.formValid) {
      // Loop validates field if good in place of user interaction\
      for (const field in this.formValidation.formErrors) {
        if (field.includes('basePoint')) {
          const newName = field.replace('basePoint', '');
          const value = this.newLandLevelingForm.basePoint[newName];
          this.validateField(field, value);
        } else {
          const value = this.newLandLevelingForm[field];
          this.validateField(field, value);
        }
      }
      // If invalid - return and halt execution
      if (!this.formValidation.formValid) {
        return;
      }
    }

  }

  @action.bound async postGetBestFit() {
    this.exclude('bestFit');
    // If user doesn't have a z value we set it to -1 so backend knows to calculate it.
    const newBasePoint: IpointData = Object.assign({}, this.newLandLevelingForm.basePoint);
    if (this.newLandLevelingForm.basePoint.z <= 0) {
      newBasePoint.z = -1;
    }

    const postGetBestFitJSON: IpostGetBestFitData = {
      basePoint: newBasePoint,
      designElementId: designStore.currentSurface.designElementId,
      useWholeField: this.newLandLevelingForm.useWholeField,
      boundaryPoints: this.newLandLevelingForm.boundaryPoints,
    };

    landLevelingDesignStore.showLoader = true;
    this.isLoading = true;
    this.error = false;
    this.errorMsg = '';
    try {
      const response = (await structureController.getLandLevelingBestFitUsingPOST(postGetBestFitJSON)).data;
      if (response.basePoint) {
        this.newLandLevelingForm.xslope = toDecimalPrecision(response.xslope!, 6);
        this.newLandLevelingForm.yslope = toDecimalPrecision(response.yslope!, 6);
        // this.newLandLevelingForm.bestFit = response.bestFit; // This seems to always return false
        this.newLandLevelingForm.bestFit = true;
        // if (!this.lockBasePointZ) {
        this.newLandLevelingForm.basePoint.x = response.basePoint.x!;
        this.newLandLevelingForm.basePoint.y = response.basePoint.y!;
        this.newLandLevelingForm.basePoint.z = response.basePoint.z!;
        // }
      }
      landLevelingDesignStore.showLoader = false;
      this.isLoading = false;
      this.error = false;
      this.errorMsg = '';
    } catch (error) {
      this.showOverrideButtons = false;
      if ((error as any).response && (error as any).response.data && (error as any).response.data.errorMessage) {
        MessageCenter.addError((error as any).response.data.errorMessage, [], (error as any).response.data.originalErrorCode);
      } else {
        MessageCenter.addError((error as any).message);
      }
      landLevelingDesignStore.showLoader = false;
      this.error = true;
      this.isLoading = false;
    }
    if (!this.error) {
      this.checkStructurePendingEdits('bestFit');
    }

    //verify the form is valid after best fits are posted.
    if (!this.formValidation.formValid) {
      // Loop validates field if good in place of user interaction\
      for (const field in this.formValidation.formErrors) {
        if (field.includes('basePoint')) {
          const newName = field.replace('basePoint', '');
          const value = this.newLandLevelingForm.basePoint[newName];
          this.validateField(field, value);
        } else {
          const value = this.newLandLevelingForm[field];
          this.validateField(field, value);
        }
      }
      // If invalid - return and halt execution
      if (!this.formValidation.formValid) {
        return;
      }
    }
  }

  @action.bound async postUpdateDesignElement(mapView) {
    designStore.currentDesignElementJson = null;
    const checkNameDuplicate = await this.checkNameDuplicate();
    if (checkNameDuplicate) {
      return;
    }
    if (landLevelingDesignStore.pendingEdits.name) {
      landLevelingDesignStore.newLandLevelingResults!.name = landLevelingDesignStore.newDesignForm.name;
    }
    const newLandLevelingResultsCopy = landLevelingDesignStore.newLandLevelingResults as any;
    newLandLevelingResultsCopy.designedContours = null;
    newLandLevelingResultsCopy.designedTinTriangles = null;
    const postJson: IupdateDesignElementPostData = {
      designElementId: landLevelingDesignStore.newDesignForm.designElementId,
      designElementName: landLevelingDesignStore.newDesignForm.name,
      sessionId: designStore.projectsStore.currentSessionId,
      designElementTypeId: 15,
      json: JSON.stringify(newLandLevelingResultsCopy),
    };
    this.error = false;
    try {
      const response = (await designController.updateStructureDesignElementUsingPOST(postJson)).data;

      if (response.designElementId && this.mapView) {
        if (landLevelingDesignStore.currentDesignLayer !== undefined) {
          this.mapView.map.remove(landLevelingDesignStore.currentDesignLayer);
        }

        if (landLevelingDesignStore.newLandLevelingResults) {
          landLevelingDesignStore.displayLandLevelingGraphic(
            this.mapView,
            landLevelingDesignStore.newLandLevelingResults,
            true,
            libraryStore.projectSpatialReference!,
            response.designElementId.toString()
          );
        }
        landLevelingDesignStore.latestElementId = response.designElementId;
        this.cancelAddDesignElement(mapView);
        landLevelingDesignStore.pendingEdits.unsavedEdits = false;

        createSaveThumbnail(this.mapView).then((snapShot: any) => {
          this.saveThumbnail(snapShot, response.designElementId, response.designElementTypeId);
        });
      }
    } catch (error) {
      this.error = true;
      // this.errorMsg = `${(error as Error).message}`;
      console.error('Error:', error);
    }
  }

  @action.bound async postAddDesignElement(mapView) {
    designStore.onNewStructureTab = false;
    designStore.currentDesignElementJson = null;
    const checkNameDuplicate = await this.checkNameDuplicate();
    if (checkNameDuplicate) {
      return;
    }
    const newLandLevelingResultsCopy = landLevelingDesignStore.newLandLevelingResults as any;
    newLandLevelingResultsCopy.designedContours = null;
    newLandLevelingResultsCopy.designedTinTriangles = null;
    const addDesignExample: IaddDesignElementPostData = {
      surfaceDefId: designStore.currentSurface.surfaceDefId,
      sessionId: designStore.projectsStore.currentSessionId,
      designElementName: landLevelingDesignStore.newDesignForm.name,
      designElementTypeId: 15, // Land Leveling
      json: JSON.stringify(newLandLevelingResultsCopy),
    };
    const postJSON = addDesignExample;
    postJSON.json = JSON.stringify(landLevelingDesignStore.newLandLevelingResults);
    this.isLoading = true;
    this.error = false;
    this.errorMsg = '';
    try {
      const response = (await designController.addDesignElementUsingPOST(postJSON)).data;
      if (response.designElementId && this.mapView) {
        const responseParsed = JSON.parse(response.json!);
        if (landLevelingDesignStore.currentDesignLayer !== undefined) {
          this.mapView.map.remove(landLevelingDesignStore.currentDesignLayer);
        }
        landLevelingDesignStore.displayLandLevelingGraphic(
          this.mapView,
          responseParsed,
          false,
          libraryStore.projectSpatialReference!,
          response.designElementId.toString()
        );
        landLevelingDesignStore.latestElementId = response.designElementId;
        landLevelingDesignStore.cancelDesignElement();
        designStore.structureLayerExists = true;

        createSaveThumbnail(this.mapView).then((snapShot: any) => {
          this.saveThumbnail(snapShot, response.designElementId, response.designElementTypeId);
        });

        landLevelingDesignStore.displayedLandLevelingsIds = [];
        landLevelingDesignStore.displayedLandLevelingsIds.push(response.designElementId);
      }
      this.isLoading = false;
      this.error = false;
      this.errorMsg = '';
      landLevelingDesignStore.pendingEdits.unsavedEdits = false; //used for user leaving pop up warning
    } catch (error) {
      this.error = true;
      this.isLoading = false;
      // this.errorMsg = `${(error as Error).message}`;
      console.error('Error:', error);
    }
  }

  // form validation by field
  @action.bound async validateField(fieldName, value) {
    if (value === undefined) {
      return;
    }
    // connects form field to validation table
    const keyValid = fieldName + 'Valid';
    let fieldValid = this.formValidation[keyValid];
    const fieldValidationErrors = this.formValidation.formErrors;
    let errorCode: string = '';
    switch (fieldName) {
      //land leveling name is required
      case 'name':
        fieldValid = value !== '';
        errorCode = fieldValid ? '' : 'LandLevel_09'; //Land Leveling Name is required.
        break;
      //boundary & base point x, y, z must be set
      case 'basePointx':
        fieldValid = value > 0;
        errorCode = fieldValid ? '' : 'Common_09'; // must be set.
        break;
      case 'basePointy':
        fieldValid = value > 0;
        errorCode = fieldValid ? '' : 'Common_09'; // must be set.
        break;
      case 'basePointz':
        fieldValid = value > 0;
        errorCode = fieldValid ? '' : 'Common_09'; // must be set.
        break;
      case 'boundaryPoints':
        fieldValid = value.length > 0 || this.newLandLevelingForm.useWholeField;
        errorCode = fieldValid ? '' : 'Common_09'; // must be set.
        fieldValidationErrors['useWholeField'] = '';
        this.formValidation.useWholeFieldValid = true;
        break;
      case 'useWholeField':
        fieldValid = (value === true || this.newLandLevelingForm.boundaryPoints.length > 0);
        errorCode = fieldValid ? '' : 'Common_09'; // must be set.
        fieldValidationErrors['boundaryPoints'] = '';
        this.formValidation.boundaryPointsValid = true;
        break;
      //Sx and Sy are optional. They are default to 0.
      case 'xslope':
        fieldValid = true;
        break;
      case 'yslope':
        fieldValid = true;
        break;
      //Common_10 ensures the input should be greater than 0.
      case 'borrow':
        fieldValid = value > -1;
        errorCode = fieldValid ? '' : 'Common_10'; // should be greater than zero and cannot be a negative number.
        break;
      case 'waste':
        fieldValid = value > -1;
        errorCode = fieldValid ? '' : 'Common_10'; // should be greater than zero and cannot be a negative number.
        break;
      case 'cutFillRatio':
        fieldValid = value >= 0;
        errorCode = fieldValid ? '' : 'Common_14';  // should not be a negative number.
        break;
      case 'llStakeoutGrid':
        fieldValid = (value > 0 && Number.isInteger(+value));
        errorCode = fieldValid ? '' : 'Common_11'; // must be a positive integer.
        break;
      //Common 11 ensures that the input is a positive number.
      case 'bufferSize':
        // First check if Whole Field is selected, if so buffer must be 0
        if (this.newLandLevelingForm.useWholeField) {
          fieldValid = value === 0;
          errorCode = fieldValid ? '' : 'LandLevel_18'; // The system encountered an issue while loading Land Leveling inputs. 
        } else {
          // Standard validation when Whole Field is not selected
          fieldValid = (value > -1 && Number.isInteger(value)) || value == 0;
          errorCode = fieldValid ? '' : 'Common_11'; // must be a positive integer.
        }
        break;
    }
    this.formValidation.formErrors = fieldValidationErrors;
    await this.getErrorMessage(errorCode, this.formValidation.formErrors, fieldName);
    this.formValidation[keyValid] = fieldValid;
    this.validateForm();
  }


  // checks if there are any error msgs over entire form
  @action.bound validateForm() {
    let valid = true;
    for (const key in this.formValidation.formErrors) {
      if (!this.formValidation[key + 'Valid']) {
        valid = false;
      }
    }
    this.formValidation.formValid = valid;
  }

  @action.bound errorClass(field) {
    const errorMessage = this.formValidation.formErrors[field];
    return errorMessage.length === 0 ? '' : ' usa-input--error';
  }
  // END Form Validation model

  @action.bound exclude(funcType) {
    switch (funcType) {
      case 'centroid':
        this.excluded.centroid = true;
        this.excluded.bestFit = false;
        break;
      case 'bestFit':
        this.excluded.bestFit = true;
        this.excluded.centroid = false;
        break;
      case 'none':
        this.excluded.bestFit = false;
        this.excluded.centroid = false;
        for (const field in this.formValidation) {
          if (!field.includes('formErrors')) {
            this.formValidation[field] = false;
          }
        }
    }
  }

  //user leaving notification section
  @observable notification: any = {
    openLandLeveling: '',
  };

  @action.bound async getErrors() {
    let message = await MessageCenter.addErrorLabelFromCode('Survey_21'); // 'Please Save or Cancel Pending Edits before Leaving Surface Design.';
    message = message.replace('Surface', 'Land Leveling');
    this.notification.openLandLeveling = message;
  }

  @action.bound userLeavingWarningOnOk() {
    return false;
  }
  async saveThumbnail(snapShot: any, designElementId: number | undefined, designElementTypeId: number | undefined) {
    if (snapShot && designElementTypeId !== undefined && designElementId !== undefined) {
      const payloadForMap = {
        designElementId: designElementId,
        designElementTypeId: designElementTypeId,
        profileImageBase64String: undefined,
        profileOrCrossSection: undefined,
        stationId: undefined,
        structureMapImageBase64String: snapShot.dataUrl,
        structureProfilesId: 0,
        twoDimensionalGeometryJson: undefined
      };

      try {
        await SETAPI.StructureProfileController.generateUpdateStructureMapImageUsingPOST(payloadForMap);
      } catch (error) {
        throw error;
      }
    }
  }

}


// NewLandLevelingVM Interfaces

interface IpointData {
  pointName: string;
  x: number;
  y: number;
  z: number;
}
